import React from 'react';
import { useRouter } from 'next/compat/router';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUser from 'src/hooks/use-user';
import useUI from 'src/hooks/use-ui';
import useDispensaryMenuSections from 'src/dispensary/hooks/use-dispensary-menu-sections';
import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';
import { useRecentlyViewedCarouselEnabled } from 'src/utils/personalization/hooks/use-personalization-enabled';
import { useCommerceComponents } from 'src/theme/provider/commerce-components/context';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import { useProductsForHomePage } from 'src/dispensary/hooks/use-products-for-home-page';
import { PersonalizedProducts } from 'src/components/carousels/products-carousels/personalized-products-carousel';
import { CarouselTitleSlotProvider } from 'src/components/carousel-v2/carousel-title-slot-context';

import { CollectionCarousel as CollectionItemsSection } from 'src/components/carousels/products-carousels/collection-carousel';
import {
  BrandCarousel as BrandSection,
  CategoryCarousel as CategorySection,
  SpecialsCarousel as SpecialsSection,
  SubcategoryCarousel as SubcategorySection,
  StaffPicksCarousel as StaffPicksSection,
  TopSellersCarousel as TopSellersSection,
} from '../carousels/generic-carousels';

import { CustomCarousel as CustomSection } from '../carousels/custom-carousels';
import { SpecialOffersCarousel as OffersSection } from '../carousels/offers-carousels';
import { RecentlyViewedCarousel as RecentlyViewedSection } from '../carousels/products-carousels';
import { createCarouselSequence } from './helpers';
import ImageSection from '../carousels/custom-carousels/image-section';

// Mirrors PersonalizedProductsSectionRender in
// src/cms/menu-landing/blocks/personalized-carousels.tsx, the equivalent Puck CMS block.
function RecommendedForYouSection() {
  const { dispensary } = useDispensary();
  const { isKiosk } = useUI();
  const isKioskProEnabled = useKioskPro();
  const { personalizedProductsQuery } = useProductsForHomePage({ dispensaryId: dispensary?.id ?? '' });

  if (isKiosk && !isKioskProEnabled) {
    return null;
  }

  return <PersonalizedProducts personalizedProductsQuery={personalizedProductsQuery} />;
}

const CarouselSection = React.memo(({ section, index, dispensaryState, hideSpecials, showRecentlyViewedCarousel }) => {
  switch (section.sectionType) {
    case 'BRAND':
      return <BrandSection key={section.id} section={section} sectionIndex={index} />;
    case 'CATEGORY':
      return (
        <CategorySection key={section.id} section={section} sectionIndex={index} dispensaryState={dispensaryState} />
      );
    case 'RECENTLY_VIEWED':
      return (
        showRecentlyViewedCarousel && <RecentlyViewedSection key={section.id} section={section} sectionIndex={index} />
      );
    case 'RECOMMENDED_FOR_YOU':
      return <RecommendedForYouSection key={section.id} />;
    case 'SPECIALS':
      return !hideSpecials && <SpecialsSection key={section.id} section={section} sectionIndex={index} />;
    case 'SUBCATEGORY':
      return (
        <SubcategorySection key={section.id} section={section} sectionIndex={index} dispensaryState={dispensaryState} />
      );
    case 'STAFF_PICKS':
      return <StaffPicksSection key={section.id} section={section} sectionIndex={index} />;
    case 'TOP_SELLERS':
      return <TopSellersSection key={section.id} section={section} sectionIndex={index} />;
    case 'CUSTOM':
      return <CustomSection key={section.id} section={section} sectionIndex={index} />;
    case 'OFFERS':
      return <OffersSection key={section.id} section={section} />;
    case 'COLLECTION':
      return <CollectionItemsSection key={section.id} section={section} sectionIndex={index} />;
    case 'IMAGE':
      return <ImageSection key={section.id} section={section} />;
    default:
      return null;
  }
});

CarouselSection.displayName = 'CarouselSection';

export const HomepageCarouselSections = observer(() => {
  const { dispensary } = useDispensary();
  const router = useRouter();
  const dispensaryId = dispensary?.id;
  const { isLoggedIn } = useUser();
  const { isKiosk } = useUI();

  const personalizationRecentlyViewedEnabled = useRecentlyViewedCarouselEnabled();
  const showRecentlyViewedCarousel = !isKiosk && personalizationRecentlyViewedEnabled && isLoggedIn;

  const hideSpecials = useHideDiscountFromConsumer(dispensary);

  const { menuSections } = useDispensaryMenuSections();
  const { iterables } = useCommerceComponents();
  const interstitials = iterables.StoreFrontCarouselInterstitials();
  const carouselSequence = createCarouselSequence(menuSections, interstitials);

  if (!dispensaryId) {
    return null;
  }

  const { hideTitle } = router?.query ?? {};
  const dispensaryState = dispensary.location?.state;

  return (
    <Sections data-testid='homepage-carousel-sections'>
      {carouselSequence.map((item, index) => {
        if (item.type === 'interstitial') {
          const Interstitial = item.interstitial;

          // eslint-disable-next-line react/no-array-index-key
          return <Interstitial key={`interstitial-${index}`} />;
        }

        const sectionWithTitle = hideTitle ? { ...item.carousel, hideTitle } : item.carousel;

        return (
          <CarouselTitleSlotProvider key={sectionWithTitle.id} carouselId={sectionWithTitle.id}>
            <CarouselSection
              section={sectionWithTitle}
              index={index}
              dispensaryState={dispensaryState}
              hideSpecials={hideSpecials}
              showRecentlyViewedCarousel={showRecentlyViewedCarousel}
            />
          </CarouselTitleSlotProvider>
        );
      })}
    </Sections>
  );
});

const Sections = styled.div``;
